import React, { useEffect, useState } from 'react';
import { Modal, Box, Switch, Tooltip, FormControl, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { useTranslation, Trans } from 'react-i18next';
import { PatientInfoData } from '../../types/Booking/booking';
import EastIcon from '@mui/icons-material/East';
import styles from '../../components/BookingPage/Step/PatientInfo/PatientInfo.module.scss';
import professionalStyles from '../../pages/ProfessionalDetail/professional-detail.module.scss';
import { FileDropzone } from '../ui/FileDropzone/FileDropzone';
import { TextFieldUi } from '../ui/TextFieldUi/TextFieldUi';
import { patientInfoSchema } from '../../utils/shemas/PatientInfoSchema';
import { localStorageHelper } from '../../utils/localStorageHelper';
import Loading from '../ui/Loading/Loading';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import VideocamIcon from '@mui/icons-material/Videocam';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import DomainVerificationIcon from '@mui/icons-material/DomainVerification';

type FormErrors = Partial<Record<keyof PatientInfoData, string>>;

interface BookingModalProps {
    open: boolean;
    onClose: () => void;
    onConfirmBooking: (patientInfo: PatientInfoData) => Promise<void>;
    consultPrice?: string | number | null;
    modality?: 'Presencial' | 'Virtual' | 'Presencial/Virtual' | string | null;
}

export const BookingModal = ({ open, onClose, onConfirmBooking, consultPrice, modality }: BookingModalProps) => {
    const { t } = useTranslation();
    const [patientInfo, setPatientInfo] = useState<PatientInfoData>({
        name: '',
        number: '',
        email: '',
        reserveReason: '',
        quoteForInvited: false,
        photo: null,
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [bookingModality, setBookingModality] = useState<string>('');
    const [modalityTooltipOpen, setModalityTooltipOpen] = useState(false);

    useEffect(() => {
        const savedDetails = localStorageHelper.getItem<any>('bookingDetails');
        const savedType = savedDetails?.consult_type;

        if (savedType && (savedType === 'Presencial' || savedType === 'Virtual')) {
            setBookingModality(savedType);
        } else {
            if (modality === 'Presencial') setBookingModality('Presencial');
            else if (modality === 'Virtual') setBookingModality('Virtual');
            else setBookingModality('');
        }
    }, [modality, open]);

    useEffect(() => {
        if (!patientInfo.quoteForInvited) {
            try {
                const userDataString = localStorageHelper.getItem<{ first_name: string, email: string, details?: { phone?: string } }>('user');
                if (userDataString) {
                    setPatientInfo((prev) => ({
                        ...prev,
                        name: userDataString.first_name || '',
                        email: userDataString.email || '',
                        number: userDataString.details?.phone || '',
                    }));
                }
            } catch (error) {
                console.error(t('landing.BookingModal.errorReadingUserData'), error);
            }
        }
        else {
            setPatientInfo((prev) => ({
                ...prev,
                name: '',
                email: '',
            }));
        }
    }, [patientInfo.quoteForInvited, t]);

    const clearErrorOnChange = (fieldName: keyof PatientInfoData) => {
        if (errors[fieldName]) {
            setErrors(prevErrors => {
                const newErrors = { ...prevErrors };
                delete newErrors[fieldName];
                return newErrors;
            });
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { id, value, type } = e.target;
        const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
        clearErrorOnChange(id as keyof PatientInfoData);
        setPatientInfo({
            ...patientInfo,
            [id]: newValue,
        });
    };

    const handleFileSelect = (file: File | null) => {
        clearErrorOnChange('photo');
        setPatientInfo({
            ...patientInfo,
            photo: file,
        });
    };


    const [loading, setLoading] = useState(false);

    const handleConfirmClick = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const validationResult = patientInfoSchema.safeParse(patientInfo);
        let hasError = false;

        if (!validationResult.success) {
            const formattedErrors: FormErrors = {};
            for (const issue of validationResult.error.issues) {
                formattedErrors[issue.path[0] as keyof PatientInfoData] = issue.message;
            }
            setErrors(formattedErrors);
            hasError = true;
        }

        if (modality === 'Presencial/Virtual' && !bookingModality) {
            setModalityTooltipOpen(true);
            hasError = true;
        }

        if (hasError) return;

        setErrors({});
        setLoading(true);

        try {
            let finalType = '';
            if (modality === 'Presencial') finalType = 'Presencial';
            else if (modality === 'Virtual') finalType = 'Virtual';
            else finalType = bookingModality;

            const currentBookingDetails = localStorageHelper.getItem<any>('bookingDetails') || {};
            localStorageHelper.setItem('bookingDetails', {
                ...currentBookingDetails,
                consult_type: finalType
            });

            await onConfirmBooking(patientInfo);
        } finally {
            setLoading(false);
        }
    };

    const renderModalitySelection = () => {
        if (!modality) return null;

        const displayModality = bookingModality || modality;

        return (
            <div className={professionalStyles['professional-detail__modality-container']} style={{ marginTop: '1rem', marginBottom: 0 }}>
                <Tooltip
                    open={modalityTooltipOpen}
                    title={t('landing.professionalsDetail.bookingPanel.tooltipSelectModality')}
                    placement="top"
                    arrow
                    disableHoverListener
                    disableFocusListener
                    disableTouchListener
                    componentsProps={{
                        tooltip: {
                            sx: {
                                bgcolor: '#d32f2f',
                                fontSize: '13px'
                            }
                        },
                        arrow: {
                            sx: {
                                color: '#d32f2f'
                            }
                        }
                    }}
                >
                    <div style={{ width: '100%' }}>
                        {modality === 'Presencial/Virtual' && (
                            <div className={professionalStyles['professional-detail__modality-selection']}>
                                <FormControl component="fieldset" fullWidth>
                                    <RadioGroup
                                        row
                                        aria-label="modality"
                                        name="modality-group"
                                        value={bookingModality}
                                        onChange={(e) => {
                                            setBookingModality(e.target.value);
                                            setModalityTooltipOpen(false);
                                        }}
                                        className={professionalStyles['professional-detail__radio-group']}
                                    >
                                    </RadioGroup>
                                </FormControl>
                            </div>
                        )}

                        {displayModality === 'Presencial' && (
                            <div className="bw-booking-note">
                                <svg viewBox="0 0 24 24" aria-hidden="true" stroke="currentColor" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20V7h6v13M10 20V4h10v16M2 20h20M7 10h.01M7 14h.01M15 8h.01M15 12h.01M15 16h.01"></path></svg>
                                <span>
                                    <Trans i18nKey="landing.BookingModal.modalityPresencial">
                                        Esta cita será realizada de forma <strong>presencial.</strong>
                                    </Trans>
                                </span>
                            </div>
                        )}

                        {displayModality === 'Virtual' && (
                            <div className="bw-booking-note">
                                <VideocamIcon />
                                <span>
                                    <Trans i18nKey="landing.BookingModal.modalityVirtual">
                                        Esta cita será realizada de forma <strong>virtual.</strong>
                                    </Trans>
                                </span>
                            </div>
                        )}
                    </div>
                </Tooltip>
            </div>
        );
    };

    return (
        <>
            <Modal style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflowY: 'auto',
            }} open={open} onClose={loading ? () => { } : onClose}>
                <div style={{
                    outline: 'none',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '24px 12px',
                    margin: 'auto',
                }}>
                    {loading && (
                        <Loading fullscreen text={t('landing.BookingModal.processingRequest')} />
                    )}
                    <article className="bw-modal-card bw-modal-card--booking" role="dialog" aria-modal="true" aria-labelledby="booking-form-title">
                        <header className="bw-booking-header">
                            <div>
                                <p className="bw-modal-eyebrow">{t('landing.BookingModal.bookingEyebrow')}</p>
                                <h1 className="bw-booking-title" id="booking-form-title">{t('landing.BookingModal.completeDataTitle')}</h1>
                                <p className="bw-booking-copy">{t('landing.BookingModal.completeDataCopy')}</p>
                            </div>
                            <button className="bw-payment-close" type="button" aria-label="Cerrar" onClick={onClose}>×</button>
                        </header>

                        <form onSubmit={handleConfirmClick}>
                            <div className="bw-booking-body">
                                <section className="bw-booking-panel" aria-label="Datos personales">
                                    <TextFieldUi
                                        label={t('landing.BookingModal.name')}
                                        id="name"
                                        placeholder="David John"
                                        value={patientInfo.name}
                                        onChange={handleChange}
                                        error={!!errors.name}
                                        helperText={errors.name}
                                        fullWidth
                                    />
                                    <TextFieldUi
                                        label={t('landing.BookingModal.email')}
                                        id="email"
                                        type="email"
                                        placeholder="email@gmail.com"
                                        value={patientInfo.email}
                                        onChange={handleChange}
                                        error={!!errors.email}
                                        helperText={errors.email}
                                        fullWidth
                                    />
                                    <TextFieldUi
                                        label={t('landing.BookingModal.number')}
                                        id="number"
                                        type="tel"
                                        placeholder="(123) 456 - 789"
                                        value={patientInfo.number}
                                        onChange={handleChange}
                                        error={!!errors.number}
                                        helperText={errors.number}
                                        fullWidth
                                    />

                                    {renderModalitySelection()}
                                </section>

                                <section className="bw-booking-panel" aria-label="Motivo y adjuntos">
                                    <label className="bw-field">
                                        <span>{t('landing.BookingModal.consultationReason')}</span>
                                        <textarea
                                            id="reserveReason"
                                            placeholder={t('landing.BookingModal.consultationReasonPlaceholder')}
                                            value={patientInfo.reserveReason}
                                            onChange={handleChange}
                                            style={{ borderColor: errors.reserveReason ? 'red' : undefined }}
                                        />
                                        {errors.reserveReason && <p style={{ color: 'red', fontSize: '12px', margin: 0 }}>{errors.reserveReason}</p>}
                                    </label>

                                    <div className="bw-upload-label">
                                        {t('landing.BookingModal.uploadFileLabel')}
                                    </div>
                                    <div style={{ marginTop: '8px' }}>
                                        <FileDropzone
                                            onFileSelected={handleFileSelect}
                                            initialFile={patientInfo.photo}
                                            maxFileSize={5 * 1024 * 1024}
                                            acceptedFileTypes={{ 'image/jpeg': [], 'image/png': [], 'application/pdf': [] }}
                                        />
                                    </div>
                                    {errors.photo && <p style={{ color: 'red', fontSize: '12px', margin: 0 }}>{errors.photo}</p>}
                                </section>
                            </div>

                            <footer className="bw-booking-footer">
                                <div className="bw-booking-price" aria-label="Valor de la consulta">
                                    <span className="bw-booking-price__icon" aria-hidden="true">$</span>
                                    <span><small>{t('landing.BookingModal.consultationValue', 'Valor')}</small><strong>${consultPrice || '0'}</strong></span>
                                </div>
                                <label className="bw-switch-row">
                                    <span>{t('landing.BookingModal.appointmentForGuest')}</span>
                                    <input
                                        type="checkbox"
                                        id="quoteForInvited"
                                        checked={patientInfo.quoteForInvited}
                                        onChange={handleChange}
                                        aria-label={t('landing.BookingModal.appointmentForGuest')}
                                    />
                                    <span className="bw-switch" aria-hidden="true"></span>
                                </label>
                                <button className="bw-btn bw-btn-primary bw-booking-cta" type="submit" disabled={loading}>
                                    {loading ? t('landing.BookingModal.loading') : t('landing.BookingModal.continueReservation')} <span aria-hidden="true">→</span>
                                </button>
                            </footer>
                        </form>
                    </article>
                </div>
            </Modal>
        </>
    );
};

export default BookingModal;